import currency from "currency.js";
import moment from "moment";
import { ReactNode } from "react";
import { COLORS } from "../../../constants/design/colors";
import { nanosToSeconds } from "../../../utils/date";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../../utils/format";
import { ViewDetailsRow } from "../../shared/ViewDetailsModal/style";

interface IDetailRowProps {
  label: string;
  value: ReactNode;
  /** When false, the row is skipped. */
  visible?: boolean;
}

/** A label/value row that hides itself when value is empty or `visible: false`. */
export function DetailRow({ label, value, visible = true }: IDetailRowProps) {
  if (!visible) return null;
  if (value === undefined || value === null || value === "") return null;
  return (
    <ViewDetailsRow>
      <span>{label}</span>
      <span>{value}</span>
    </ViewDetailsRow>
  );
}

/**
 * Signed numeric value rendered with positive/negative color. Returns null
 * (and therefore hides the row in `DetailRow`) when the value is zero.
 */
export function SignedValue({
  value,
  precision = 2,
  invert = false,
}: {
  value?: string | number;
  precision?: number;
  invert?: boolean;
}) {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return <>-</>;
  const display = invert ? -n : n;
  return (
    <span
      style={{
        color: display >= 0 ? COLORS.up.one : COLORS.down.one,
      }}
    >
      {formatCurrencyWithSubscript(display, {
        precision,
        showPositiveSign: true,
      })}
    </span>
  );
}

export function FormattedPrice({
  value,
  precision,
}: {
  value?: string | number;
  precision: number;
}) {
  if (value === undefined || value === null || value === "") return <>-</>;
  const n = Number(value);
  if (!Number.isFinite(n)) return <>-</>;
  return <>{currency(n, { precision }).format()}</>;
}

export function FormattedSize({
  value,
  precision,
}: {
  value?: string | number;
  precision: number;
}) {
  if (value === undefined || value === null || value === "") return <>-</>;
  return <>{formatSizePrecision(value as any, precision)}</>;
}

export function FormattedTime({
  value,
  unit,
}: {
  value?: string | number;
  unit: "seconds" | "nanos";
}) {
  if (value === undefined || value === null || value === "") return <>-</>;
  const seconds =
    unit === "nanos" ? nanosToSeconds(String(value)) : Number(value);
  if (!Number.isFinite(seconds)) return <>-</>;
  // Local time in the user's browser timezone — matches the table cells.
  return <>{moment.unix(seconds).format("DD MMM YY HH:mm:ss")}</>;
}

/**
 * Size cell that optionally renders a USD notional underneath.
 *
 * When `filledAmount` is provided and differs from `amount`, both the
 * size and the USD value render as `intended / filled` to surface
 * partial fills clearly. Otherwise the USD line is a single value.
 */
export function SizeWithUsd({
  amount,
  filledAmount,
  price,
  amountPrecision,
  pricePrecision = 2,
}: {
  amount?: string | number;
  filledAmount?: string | number;
  price?: string | number;
  amountPrecision: number;
  pricePrecision?: number;
}) {
  if (amount === undefined || amount === null || amount === "") return <>-</>;

  const amountText = formatSizePrecision(amount as any, amountPrecision);
  const priceN = Number(price);
  const amountN = Number(amount);

  const hasFilled = filledAmount !== undefined && filledAmount !== null;
  const filledN = hasFilled ? Number(filledAmount) : amountN;
  const showSplit = hasFilled && filledN !== amountN;

  const filledText = hasFilled
    ? formatSizePrecision(filledAmount as any, amountPrecision)
    : undefined;
  const sizeLine = showSplit ? `${amountText} / ${filledText}` : amountText;

  const usdLine = (() => {
    if (!Number.isFinite(priceN) || !priceN) return undefined;
    if (!Number.isFinite(amountN) || !amountN) return undefined;
    const amountUsd = currency(amountN * priceN, {
      precision: pricePrecision,
    }).format();
    if (!showSplit) return amountUsd;
    const filledUsd = currency(filledN * priceN, {
      precision: pricePrecision,
    }).format();
    return `${amountUsd} / ${filledUsd}`;
  })();

  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <span>{sizeLine}</span>
      {usdLine && <span style={{ opacity: 0.6 }}>{usdLine}</span>}
    </span>
  );
}
